<template>
  <modal
    id="productZoomGallery"
    :is-open="openModal"
    full-width="true"
    @close="
      () => {
        $emit('close')
      }
    "
  >
    <transition name="fade" mode="out-in">
      <div class="gallery-container" :class="{ '-show': isTransitionEnd }">
        <hooper
          :settings="hooperSettings"
          ref="galleryCarousel"
          class="-white"
          @slide="updateProgress"
          @updated="updateActiveSlide"
        >
          <slide v-for="(image, index) in pictures" :key="index" class="slide">
            <ProductZoomGalleryItem v-if="canLoadSlide(index)" :item="image" />
          </slide>
        </hooper>
        <div class="hooper-navigation container" v-if="pictures.length > 1">
          <button
            ref="prevButton"
            type="button"
            class="hooper-prev black-arrow"
            :aria-label="$t('Previous slide')"
            @click="() => $refs.galleryCarousel.slidePrev()"
          ></button>
          <button
            ref="nextButton"
            type="button"
            class="hooper-next black-arrow"
            :aria-label="$t('Next slide')"
            @click="() => $refs.galleryCarousel.slideNext()"
          ></button>
        </div>
        <ul ref="progress" class="progress-default" v-if="pictures.length > 1">
          <li
            v-for="(s, i) in pictures"
            :key="`${i}-default`"
            :index="i"
            :class="{
              '-active':
                currentSlide === i ||
                (i === 0 && currentSlide === pictures.length) ||
                (i === pictures.length - 1 && currentSlide === -1)
            }"
            ref="buttons"
          >
            <button
              type="button"
              @click="slideTo(i)"
              :aria-label="progressAriaLabel(i)"
            >
              <span class="area"></span>
            </button>
          </li>
        </ul>
      </div>
    </transition>
  </modal>
</template>

<script>
import { Hooper, Slide } from 'hooper'
import { ReadyHook } from 'vue-asyncready'
import { mapActions } from 'vuex'
import { isServer } from '@vue-storefront/core/helpers'

import Modal from '../components/utils/Modal'
import ProductZoomGalleryItem from './ProductZoomGalleryItem.vue'

export default {
  props: ['openModal', 'pictures', 'galleryIndex'],
  name: 'ProductZoomGallery',
  components: {
    Modal,
    Hooper,
    Slide,
    ProductZoomGalleryItem
  },
  mixins: [ReadyHook],
  data() {
    return {
      currentSlide: 0,
      loadedSlides: [],
      isZoomed: false,
      isTransitionEnd: false,
      hooperSettings: {
        transition: 600,
        mouseDrag: false,
        touchDrag: false,
        keysControl: true,
        wheelControl: false,
        infiniteScroll: true,
        centerMode: true
      }
    }
  },
  methods: {
    ...mapActions('feedback', ['show']),
    onZoomChanged(updatedZoom) {
      this.isZoomed = updatedZoom
    },
    canLoadSlide(index) {
      if (this.loadedSlides.includes(index)) {
        return true
      }
      let tmpCurrentSlide = this.currentSlide

      if (this.currentSlide < 0) {
        // is infinite slide and the user is scrolling back in the previous sides
        tmpCurrentSlide = -Math.abs(
          this.pictures.length - Math.abs(this.currentSlide)
        )
      }

      const diff = Math.abs(Math.abs(tmpCurrentSlide) - index)

      // Load only the first slide in the server to avoid paralle download during the page load
      // Eager lazy attribute will be used to prevent browser lazy loading and use only in JS lazy load
      if (
        (isServer && diff === 0) ||
        (!isServer && (diff <= 1 || diff === 0))
      ) {
        this.loadedSlides = [...this.loadedSlides, index]
        return true
      }

      return false
    },
    updateProgress(payload) {
      this.currentSlide = payload.currentSlide
    },
    slideTo(i) {
      this.$refs.galleryCarousel.slideTo(i)
    },
    progressAriaLabel(i) {
      return (i === 'undefined' && this.currentSlide === 0) ||
        (i && this.currentSlide === i + 1)
        ? this.$t('Slide') + ' ' + i + ' ' + this.$t('(current slide)')
        : this.$t('Slide') + ' ' + i
    },
    updateActiveSlide() {
      if (this.galleryIndex === 0) return // Skip the first slide
      this.slideTo(this.galleryIndex)
    }
  },
  watch: {
    // This is a workaround to update the carousel width after the modal is opened
    openModal(newVal) {
      if (newVal == true) {
        setTimeout(() => {
          this.$refs.galleryCarousel.updateWidth()
          this.isTransitionEnd = true
        }, 500) // 500ms is the transition time of the modal
      } else {
        setTimeout(() => {
          this.isTransitionEnd = false
        }, 500)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/core';

.modal {
  .fade-enter,
  .fade-leave-to {
    position: static;
  }
}

.modal /deep/.content {
  height: 100%;
  width: 100%;
}

.gallery-container {
  height: 100%;
  width: 100%;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;

  &.-show {
    opacity: 1;
  }
}

.slide {
  display: flex;
  justify-content: center;
  align-items: center;
}

.slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease-in-out;
  cursor: zoom-in;
}

.slide img.zoomed {
  transform: scale(2);
  cursor: zoom-out;
}

.hooper {
  outline: none;
  height: 100%;

  /deep/ .hooper-slide {
    position: relative;
    overflow: hidden;
  }

  & + .hooper-navigation {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    transform: translate(-50%, -50%);

    &.container {
      max-width: none;
    }

    .hooper-prev {
      left: 1rem;
      outline: none;

      @include bp(md) {
        left: 4rem;
      }

      &:before {
        content: '\e903';
      }

      &:hover {
        &:before {
          @include pillPrevHover();
        }
      }

      &.hidden-button {
        transform: translate(50%, -50%);
      }
    }

    .hooper-next {
      right: 1rem;
      outline: none;

      @include bp(md) {
        right: 4rem;
      }

      &:before {
        content: '\e925';
      }

      &:hover {
        &:before {
          @include pillNextHover();
        }
      }

      &.hidden-button {
        transform: translate(-50%, -50%);
      }
    }

    button {
      color: var(--zenith-component-slider-full-page-button);
      padding: 1.3rem;
      border-radius: 100%;
      box-shadow: 0 1px 1px 0
        var(--zenith-component-slider-full-page-button-shadow);
      border: 1px solid var(--zenith-component-slider-full-page-button);

      transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1),
        background 0.2s ease-in-out,
        opacity 0.3s cubic-bezier(0.39, 0.575, 0.565, 1) 100ms;

      &.hidden-button {
        opacity: 0;
        transition: transform 0s cubic-bezier(0.165, 0.84, 0.44, 1) 0.3s,
          background 0.2s ease-in-out,
          opacity 0.3s cubic-bezier(0.39, 0.575, 0.565, 1);
      }

      svg {
        display: none;
      }

      &:before {
        display: block;
        font-family: 'zenith', sans-serif !important;
        speak: none;
        font-size: em(20);
        line-height: 2rem;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;

        /* Better Font Rendering =========== */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      &.black-arrow {
        color: var(--zenith-component-product-header-arrow-first);
        background-color: var(
          --zenith-component-slider-full-page-button-hover-background
        );
      }

      &:hover,
      &:active {
        color: var(--zenith-component-slider-full-page-button-hover);
        border-color: var(
          --zenith-component-slider-full-page-button-hover-border
        );
      }
    }
  }
}

.progress-default {
  position: absolute;
  bottom: 3rem;

  li {
    width: 100%;
    transition: width 0.8s cubic-bezier(0.165, 0.84, 0.44, 1);
    display: table-cell;

    button {
      position: relative;
      width: 100%;
      padding: 0;
      border: none;
      display: block;
      background: transparent;

      .area {
        display: inline-block;
        width: 100%;
        height: 30px;
        background: blue;
        position: absolute;
        opacity: 0;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        z-index: 5;
        top: 50%;
        left: 0;
        width: 100%;
        height: 0.2rem;
        background: var(--zenith-component-product-header-progress-background);
        border-radius: 2px;
      }

      &:after {
        content: '';
        display: block;
        position: absolute;
        z-index: 10;
        top: 50%;
        left: 0;
        width: 0;
        height: 0.2rem;
        background: var(--zenith-component-product-header-progress-bar);
        border-radius: 2px;
      }
    }

    &.-active {
      width: 200%;

      button:after {
        width: 100%;
      }
    }

    &:not(.-active):hover {
      width: 175%;
    }
  }
}
</style>
